/** Image-group layout templates.
 *
 *  Single source of truth for the layouts the staff frontend + the
 *  kiosk launcher both render. An ``ImageGroup`` references one of
 *  these layouts by ``id``; the assigned ``ImageAsset`` for each slot
 *  is stored in ``ImageGroupItem`` rows keyed by ``position``.
 *
 *  Adding a new layout = adding an entry here + an entry in the
 *  backend validator's allowed-id set. The kiosk picks up the new
 *  layout automatically on the next deploy via the same spec.
 */

export type ImageGroupLayoutId =
  | "single"
  | "stack-2-vertical"
  | "stack-3-vertical"
  | "hero-with-thumbs";

export interface ImageGroupLayoutSpec {
  /** Stable id stored on ``ImageGroup.layout`` in the DB. */
  id: ImageGroupLayoutId;
  /** Operator-facing label in the Media Library picker. */
  label: string;
  /** Short description shown alongside the preview when picking. */
  description: string;
  /** Total slots the operator must fill (or leave blank). */
  slotCount: number;
  /** CSS Grid properties applied to the rendering container. */
  grid: {
    gridTemplateAreas: string;
    gridTemplateRows: string;
    gridTemplateColumns: string;
    gap?: string;
  };
  /** Grid-area name for each slot, in ``position`` order. */
  slotAreas: string[];
  /** ``object-fit`` default applied to every slot's ``<img>``. */
  defaultFit: "cover" | "contain";
  /** Tiny preview cells (~80×60 viewBox) used by the layout-picker
   *  dropdown. Pure data — consumers render JSX. Kept inline so it
   *  ships with the package and doesn't need a separate asset
   *  loader.
   *
   *  Shared rendering colours — light fill, darker stroke — should
   *  be applied by the consumer (uniformity across consumers vs.
   *  hard-coding theme values here). */
  previewCells: { x: number; y: number; w: number; h: number }[];
}

/** Shorthand for the preview-cell list. */
function cells(...boxes: [number, number, number, number][]): { x: number; y: number; w: number; h: number }[] {
  return boxes.map(([x, y, w, h]) => ({ x, y, w, h }));
}

export const IMAGE_GROUP_LAYOUTS: Record<ImageGroupLayoutId, ImageGroupLayoutSpec> = {
  single: {
    id: "single",
    label: "Single",
    description: "One image filling the corner.",
    slotCount: 1,
    grid: {
      gridTemplateAreas: '"a"',
      gridTemplateRows: "1fr",
      gridTemplateColumns: "1fr",
    },
    slotAreas: ["a"],
    defaultFit: "cover",
    previewCells: cells([4, 4, 72, 52]),
  },
  "stack-2-vertical": {
    id: "stack-2-vertical",
    label: "Stack of 2 (vertical)",
    description: "Two images stacked, equal height.",
    slotCount: 2,
    grid: {
      gridTemplateAreas: '"a" "b"',
      gridTemplateRows: "1fr 1fr",
      gridTemplateColumns: "1fr",
      gap: "4px",
    },
    slotAreas: ["a", "b"],
    defaultFit: "cover",
    previewCells: cells([4, 4, 72, 24], [4, 32, 72, 24]),
  },
  "stack-3-vertical": {
    id: "stack-3-vertical",
    label: "Stack of 3 (vertical)",
    description: "Three images stacked, equal height.",
    slotCount: 3,
    grid: {
      gridTemplateAreas: '"a" "b" "c"',
      gridTemplateRows: "1fr 1fr 1fr",
      gridTemplateColumns: "1fr",
      gap: "4px",
    },
    slotAreas: ["a", "b", "c"],
    defaultFit: "cover",
    previewCells: cells([4, 4, 72, 14], [4, 22, 72, 14], [4, 40, 72, 14]),
  },
  "hero-with-thumbs": {
    id: "hero-with-thumbs",
    label: "Hero + 2 thumbs",
    description: "One large image on the left, two smaller stacked on the right.",
    slotCount: 3,
    grid: {
      gridTemplateAreas: '"a b" "a c"',
      gridTemplateRows: "1fr 1fr",
      gridTemplateColumns: "2fr 1fr",
      gap: "4px",
    },
    slotAreas: ["a", "b", "c"],
    defaultFit: "cover",
    previewCells: cells([4, 4, 46, 52], [54, 4, 22, 24], [54, 32, 22, 24]),
  },
};

/** Ordered list for the picker dropdown — matches the natural
 *  progression from simple to complex so operators eyeball-scan it
 *  in order. */
export const IMAGE_GROUP_LAYOUT_ORDER: ImageGroupLayoutId[] = [
  "single",
  "stack-2-vertical",
  "stack-3-vertical",
  "hero-with-thumbs",
];

/** Convenience for the form's Select. */
export function imageGroupLayoutOptions(): { value: ImageGroupLayoutId; label: string; description: string }[] {
  return IMAGE_GROUP_LAYOUT_ORDER.map((id) => {
    const spec = IMAGE_GROUP_LAYOUTS[id];
    return { value: id, label: spec.label, description: spec.description };
  });
}

/** Type guard for runtime layout-id validation (used in API
 *  validators on the staff frontend before posting). The backend
 *  re-validates against its own hardcoded list. */
export function isImageGroupLayoutId(v: unknown): v is ImageGroupLayoutId {
  return typeof v === "string" && v in IMAGE_GROUP_LAYOUTS;
}

export interface SlotSize {
  width: number;
  height: number;
}

/** GCD-reduce ``width:height`` to its lowest integer terms, e.g.
 *  ``1920,1080 -> "16:9"``. Meant for a whole canvas, which always
 *  reduces to a clean number — never call this on a group's individual
 *  slot size (a grid fraction rarely reduces to anything nameable). */
export function simplifyRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(Math.round(width), Math.round(height)) || 1;
  return `${Math.round(width) / divisor}:${Math.round(height) / divisor}`;
}

/** One track list ("1fr 1fr", "2fr 1fr") parsed into its `fr` weights.
 *  Every layout's tracks are pure `fr` units today — nothing here reads
 *  a fixed unit (`px`/`%`), since none of the four layouts use one. */
function parseFrTracks(value: string): number[] {
  return value
    .trim()
    .split(/\s+/)
    .map((token) => {
      const match = /^([\d.]+)fr$/.exec(token);
      if (!match) throw new Error(`imageGroupSlotSizes: unsupported track "${token}" (only "<n>fr" is handled)`);
      return Number(match[1]);
    });
}

/** Pixel offsets and sizes for each track in a `fr`-based grid axis,
 *  after subtracting the gaps between tracks from the available space
 *  and distributing the remainder proportionally to each track's `fr`
 *  weight. Returns one `{ start, size }` per track, in track order. */
function resolveAxis(tracksSpec: string, totalSize: number, gapPx: number): { start: number; size: number }[] {
  const weights = parseFrTracks(tracksSpec);
  const totalGap = gapPx * Math.max(0, weights.length - 1);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  const available = totalSize - totalGap;
  const out: { start: number; size: number }[] = [];
  let cursor = 0;
  for (const weight of weights) {
    const size = (available * weight) / totalWeight;
    out.push({ start: cursor, size });
    cursor += size + gapPx;
  }
  return out;
}

/** Parses `gridTemplateAreas` (e.g. `'"a b" "a c"'`) into a 2D array of
 *  area names, one row per quoted string, one cell per space-separated
 *  token — the only place a slot's row/column SPAN is encoded (a name
 *  repeated across cells means that slot spans them). */
function parseAreas(gridTemplateAreas: string): string[][] {
  const rows = gridTemplateAreas.match(/"([^"]*)"/g) ?? [];
  return rows.map((row) => row.slice(1, -1).trim().split(/\s+/));
}

/** Resolves a layout's grid (rows/columns as `fr` weights + a single
 *  `gap`) against a real canvas, returning one `{width, height}` per
 *  slot in `slotAreas` order — the pixel size an uploaded image for
 *  that slot should actually be. A slot spanning multiple cells (e.g.
 *  `hero-with-thumbs`'s `"a"` covering both rows) resolves to the
 *  bounding box of every cell it occupies, gaps between those cells
 *  included — the same box the CSS grid actually paints it into. */
export function computeGroupSlotSizes(
  layoutId: ImageGroupLayoutId,
  canvasWidth: number,
  canvasHeight: number,
): SlotSize[] {
  const spec = IMAGE_GROUP_LAYOUTS[layoutId];
  const gapPx = spec.grid.gap ? Number.parseFloat(spec.grid.gap) : 0;
  const rows = resolveAxis(spec.grid.gridTemplateRows, canvasHeight, gapPx);
  const columns = resolveAxis(spec.grid.gridTemplateColumns, canvasWidth, gapPx);
  const areaGrid = parseAreas(spec.grid.gridTemplateAreas);

  return spec.slotAreas.map((areaName) => {
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;
    areaGrid.forEach((rowCells, rowIndex) => {
      rowCells.forEach((cell, colIndex) => {
        if (cell !== areaName) return;
        minRow = Math.min(minRow, rowIndex);
        maxRow = Math.max(maxRow, rowIndex);
        minCol = Math.min(minCol, colIndex);
        maxCol = Math.max(maxCol, colIndex);
      });
    });
    const top = rows[minRow].start;
    const bottom = rows[maxRow].start + rows[maxRow].size;
    const left = columns[minCol].start;
    const right = columns[maxCol].start + columns[maxCol].size;
    return { width: Math.round(right - left), height: Math.round(bottom - top) };
  });
}
